// src/frontend/src/pages/About.tsx - Updated with real founder information
import React from 'react';
import { Link } from 'react-router-dom';
import HomeLayout from '../components/Layout/HomeLayout';

const About: React.FC = () => {
  return (
    <HomeLayout>
      <div className="bg-white">
        {/* Hero Section */}
        <div className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
            <div className="absolute top-40 right-10 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
          </div>
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
            <div className="text-center">
              <h1 className="text-4xl md:text-6xl font-extrabold text-white tracking-tight mb-6">
                About PM Coach
              </h1>
              <p className="max-w-3xl mx-auto text-xl text-gray-300 leading-relaxed">
                We're on a mission to help project managers master the conversations that make or break their careers. 
                Founded by an experienced AI leader who understands the real challenges PMs face every day.
              </p>
            </div>
          </div>
        </div>

        {/* Mission Section */}
        <div className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-extrabold text-gray-900 mb-6">
                  Why PM Coach Exists
                </h2>
                <div className="space-y-4 text-lg text-gray-700">
                  <p>
                    <strong>75% of projects fail due to poor stakeholder communication.</strong> Yet most PM training 
                    focuses on processes, frameworks, and tools – not the daily conversations that actually determine project success.
                  </p>
                  <p>
                    We built PM Coach because we were tired of seeing talented PMs struggle with executive pushback, 
                    scope creep, and difficult stakeholders – not because they didn't know the theory, but because 
                    they hadn't practiced the conversations.
                  </p>
                  <p>
                    PM Coach bridges the gap between certification knowledge and real-world competence through 
                    AI-powered practice that adapts to your responses and provides immediate, actionable feedback.
                  </p>
                </div>
              </div>
              <div className="relative">
                <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white">
                  <h3 className="text-2xl font-bold mb-6">Our Impact</h3>
                  <div className="grid grid-cols-2 gap-6">
                    <div className="text-center">
                      <div className="text-3xl font-bold">5,000+</div>
                      <div className="text-blue-100">PMs Trained</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold">28%</div>
                      <div className="text-blue-100">Improvement in Stakeholder Satisfaction</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold">85%</div>
                      <div className="text-blue-100">Report Improved Confidence</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold">47%</div>
                      <div className="text-blue-100">Higher Promotion Success Rate</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Founder Story */}
        <div className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-extrabold text-gray-900 mb-4">
                Meet the Founder
              </h2>
              <p className="max-w-3xl mx-auto text-xl text-gray-600">
                PM Coach was created by someone who understands both the challenge of training at scale 
                and the specific communication hurdles that project managers face every day.
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              <div className="bg-white rounded-xl p-8 shadow-lg">
                <div className="flex items-center mb-6">
                  <div className="w-20 h-20 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-2xl mr-6">
                    RV
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">Ritesh Vajariya</h3>
                    <p className="text-gray-600 text-lg">Founder & CEO, PM Coach</p>
                  </div>
                </div>
                
                <div className="mb-6">
                  <p className="text-gray-700 text-lg leading-relaxed">
                    Ritesh brings a unique perspective to PM training, combining deep AI expertise with proven experience 
                    in scaling practical education programs. As founder of <strong>AI Guru Academy</strong>, he's successfully 
                    trained <strong>over 10,000 professionals globally</strong> in complex technical skills, reaching learners 
                    across 20+ countries.
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Why PM Training Matters</h4>
                    <ul className="text-gray-700 space-y-2 text-sm">
                      <li>• Witnessed how great technical minds struggle with stakeholder conversations</li>
                      <li>• Observed the gap between PM certification knowledge and real-world practice</li>
                      <li>• Experienced firsthand how communication skills determine project success</li>
                      <li>• Graduated from Harvard Business School's Negotiation Mastery program</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">What Makes PM Coach Different</h4>
                    <ul className="text-gray-700 space-y-2 text-sm">
                      <li>• Built on proven AI training methodologies that work at scale</li>
                      <li>• Designed by someone who's trained 1,000+ leaders in complex skills</li>
                      <li>• Combines technical AI capabilities with practical education experience</li>
                      <li>• Focus on specific PM challenges, not generic leadership advice</li>
                    </ul>
                  </div>
                </div>
                
                <div className="p-6 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-blue-900 mb-3">The PM Coach Vision</h4>
                  <p className="text-blue-800 leading-relaxed">
                    "After training thousands of professionals in complex AI concepts, I realized the same learning principles 
                    could revolutionize PM development. Most PMs struggle not with frameworks or processes, but with the daily 
                    conversations that make or break their projects. PM Coach provides the focused, practical training that 
                    actually prepares you for real stakeholder interactions."
                  </p>
                  <p className="text-blue-700 text-sm mt-3 italic">
                    — Ritesh Vajariya, Founder & CEO
                  </p>
                </div>
                
                <div className="mt-6 flex items-center justify-between">
                  <div className="flex space-x-4">
                    <a
                      href="https://www.linkedin.com/in/vajariya"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 transition-colors"
                      title="Connect on LinkedIn"
                    >
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                    </a>
                    <a
                      href="https://www.riteshai.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-600 hover:text-gray-800 transition-colors"
                      title="Visit Personal Website"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9" />
                      </svg>
                    </a>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">Based in</p>
                    <p className="text-sm font-medium text-gray-700">New York City</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* How We're Different */}
        <div className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-extrabold text-gray-900 mb-4">
                How We're Different
              </h2>
              <p className="max-w-3xl mx-auto text-xl text-gray-600">
                While others focus on project management theory or generic leadership advice, we're laser-focused 
                on the specific conversations that make or break PM careers.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-6">
                  <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">PM-Specific Focus</h3>
                <p className="text-gray-600">
                  Every scenario, stakeholder, and piece of feedback is designed specifically for project management challenges. 
                  No generic leadership fluff.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-6">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">AI-Powered Conversations</h3>
                <p className="text-gray-600">
                  Unlike static case studies or branching scenarios, our AI stakeholders adapt to your responses 
                  in real-time, creating truly dynamic practice sessions.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-6">
                  <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2h-2a2 2 0 00-2-2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Immediate Results</h3>
                <p className="text-gray-600">
                  See improvement in hours, not months. Get instant feedback and practice anytime, 
                  rather than waiting for the next difficult stakeholder situation.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Company Values */}
        <div className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-extrabold text-gray-900 mb-4">
                Our Values
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="text-4xl mb-4">🎯</div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">PM-First</h3>
                <p className="text-gray-600 text-sm">
                  Every feature is designed with project managers' specific needs in mind.
                </p>
              </div>

              <div className="text-center">
                <div className="text-4xl mb-4">⚡</div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Practical Impact</h3>
                <p className="text-gray-600 text-sm">
                  We measure success by real improvement in your day-to-day PM work.
                </p>
              </div>

              <div className="text-center">
                <div className="text-4xl mb-4">🚀</div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Innovation</h3>
                <p className="text-gray-600 text-sm">
                  We push the boundaries of what's possible with AI-powered training.
                </p>
              </div>

              <div className="text-center">
                <div className="text-4xl mb-4">🤝</div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Community</h3>
                <p className="text-gray-600 text-sm">
                  We're building a community of PMs who support each other's growth.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Future Vision */}
        <div className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-extrabold text-gray-900 mb-6">
                  The Future of PM Development
                </h2>
                <div className="space-y-4 text-lg text-gray-700">
                  <p>
                    We envision a world where every project manager has access to personalized, AI-powered coaching 
                    that helps them navigate any stakeholder situation with confidence.
                  </p>
                  <p>
                    Our roadmap includes advanced features like voice-based simulations, VR training environments, 
                    predictive coaching based on your communication patterns, and integration with the tools you use every day.
                  </p>
                  <p>
                    But our core mission remains the same: helping PMs master the conversations that determine project success.
                  </p>
                </div>
                
                <div className="mt-8">
                  <Link
                    to="/register"
                    className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                  >
                    Join Our Mission
                    <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </Link>
                </div>
              </div>
              
              <div className="relative">
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-8 border border-blue-200">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Join 5,000+ PMs who chose progress over perfection</h3>
                  <div className="space-y-3 text-gray-700">
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-green-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      14-day free trial with full access
                    </div>
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-green-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      No credit card required
                    </div>
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-green-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Results in hours, not months
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Contact CTA */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-extrabold text-white mb-4">
              Have Questions? We'd Love to Hear From You
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Whether you're curious about our approach, want to share feedback, or have ideas for improvement, 
              we're always excited to connect with fellow PMs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-blue-600 bg-white hover:bg-gray-50"
              >
                Get in Touch
              </Link>
              <a
                href="mailto:hello@pmcoach.app"
                className="inline-flex items-center px-8 py-3 border-2 border-white text-base font-medium rounded-md text-white hover:bg-white hover:text-blue-600"
              >
                Email Us Directly
              </a>
            </div>
          </div>
        </div>
      </div>
    </HomeLayout>
  );
};

export default About;